import type { AtomFunction, Executor, PlanFunction } from "@pdfking/planner"
import type { PageNumber } from "@pdfking/renderer-api"
import type { ExecutionContext } from "@pdfking/taskmaker"
import { Items, MethodDispatcher } from "@pdfking/tfunc"

import type { PdfEngineOptions } from "#public"
import type { MultiPagePdf } from "#public"
import type { PlottedPagePdf } from "#public"
import type { RemoteFile } from "#public"
import type { SinglePageImage } from "#public"
import type { SinglePageTask } from "#public"
import type { VirtualFile } from "#public"
import type { ZipFile } from "#public"
import type { TasksType } from "#tasks"

import { RasterNode } from "../../internal/files/RasterNode"
import { SplitNode } from "../../internal/files/SplitNode"
import { VirtualFileType } from "../types/files/VirtualFileType"
import { FileContentGUID } from "./FileContentGUID"
import { FileArray } from "./FileNodeGUID"

type Planner = Pick<Executor<TasksType, ExecutionContext>, "plan">

/** Every file but a url has a plan: a url is only ever read. */
export type PlannedFile = Exclude<VirtualFile, RemoteFile>

/** What a file's plan yields: a merge or a zip its bytes, a plot or an image
 * the page task holding them (a plot's with the colors it plotted), a split
 * page itself. */
export type FilePlanOutput<F extends PlannedFile> = F extends MultiPagePdf | ZipFile
  ? RemoteFile
  : F extends PlottedPagePdf | SinglePageImage
    ? SinglePageTask
    : F

const dispatch = MethodDispatcher<[PlannedFile, Planner, PdfEngineOptions], PlanFunction<unknown>>({
  // cut again out of the source, the page at its position in the source's
  // own split (sequential, so the take is deterministic)
  [VirtualFileType.BINARY_PAGE_PDF]: (file, planner, options) =>
    planner.plan.Take({ index: file.source_page - 1 }, SplitNode(planner, options, file.source, 0)),
  [VirtualFileType.MULTI_PAGE_PDF]: (file, { plan }, options) =>
    plan.TaskMerge(
      { options, page_count: file.pages.length as PageNumber, hash: FileContentGUID(file) },
      FileArray(plan, file.pages)
    ),
  [VirtualFileType.PLOTTED_PAGE_PDF]: (file, { plan }, options) =>
    plan.TaskPlot({ options, page: file.page, context: file.context, hash: file.guid }),
  [VirtualFileType.SINGLE_PAGE_IMAGE]: (file, planner, options) =>
    RasterNode(planner, options, file.source, file.dpi),
  [VirtualFileType.ZIP_FILE]: (file, { plan }, options) => {
    const hash = FileContentGUID(file)
    return plan.TaskZip(
      { options, hash },
      plan.Array({
        values: Items(file.files).map(([name, content]) => ({ name, content })),
        hash,
      })
    )
  },
})

/** The plan that produces a file's bytes, built from the file when they are
 * asked for — never stored on it. The task it ends in writes them under the
 * file's {@link FileContentGUID}. */
export const FilePlan = dispatch as <F extends PlannedFile>(
  file: F,
  planner: Planner,
  options: PdfEngineOptions
) => AtomFunction<FilePlanOutput<F>>
